import { useEffect, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import { type SingleSelectedValueFilter } from 'components/brokers/types'
import { TextInput } from 'slab'

interface ValidatedTextInputFilterProps extends SingleSelectedValueFilter {
  validator: (value: string) => boolean
  placeholder: string
}

const ValidatedTextInputFilter = ({
  selectedValue,
  selectValue,
  validator,
  placeholder,
}: ValidatedTextInputFilterProps) => {
  // Local state for the input value (allows typing freely)
  const [inputValue, setInputValue] = useState(selectedValue || '')
  // Track validation error state
  const [showError, setShowError] = useState(false)

  // Debounce the input value to avoid triggering API requests on every keystroke
  const [debouncedInputValue] = useDebounceValue(inputValue, 500)

  // Update local state when selectedValue changes externally (e.g., from URL or X button)
  useEffect(() => {
    setInputValue(selectedValue || '')
    setShowError(false)
  }, [selectedValue])

  // Update the filter when the debounced value changes and is valid
  // Skip the update if selectedValue already matches the debounced value to avoid conflicts
  useEffect(() => {
    // Trim whitespace before validation and submission
    const trimmedValue = debouncedInputValue.trim()

    // Don't update if the trimmed value matches what's already selected
    // This prevents the debounced effect from overriding external changes (like clicking X)
    if (trimmedValue === (selectedValue || '')) {
      setShowError(false)
      return
    }

    // Only update the filter (and trigger API request) if it's a valid value
    // Allow empty string to clear the filter
    if (trimmedValue === '' || validator(trimmedValue)) {
      selectValue(trimmedValue)
      setShowError(false)
    } else {
      // If invalid, show error and don't update the filter
      setShowError(true)
    }
  }, [debouncedInputValue, selectedValue, validator, selectValue])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    // Clear error while typing - it will be re-evaluated after debounce
    setShowError(false)
    // The debounced value will be updated after 500ms and trigger the validation effect above
  }

  const handleBlur = () => {
    // Trim the input value when the user leaves the field
    setInputValue((prev) => prev.trim())
  }

  return (
    <div style={{ width: '200px' }}>
      <TextInput
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        error={showError ? 'Invalid format' : undefined}
      />
    </div>
  )
}

export default ValidatedTextInputFilter
